import { createLogger, type CustomLogger } from '@heidi/runtime/logger';

let routeLogger: CustomLogger | undefined;

/**
 * Created on first use, not at module load — many suites mock
 * `@heidi/runtime/logger` with a factory that omits `createLogger`, and this
 * module is imported transitively by most HTTP-client-backed hook tests.
 * Deferring the call means importing this file never depends on the mock's
 * shape.
 */
function getRouteLogger(): CustomLogger {
  routeLogger ??= createLogger({ owner: 'multiplayer', domain: 'identity_broker' });
  return routeLogger;
}

/** Terminal outcome of a recovery-eligible 401. */
export type Auth401RecoveryRouteName =
  | 'rejected'
  | 'retry_requested'
  | 'legacy'
  | 'logout';

/** Why that outcome was reached. */
export type Auth401RecoveryReasonName =
  | 'header_missing'
  | 'no_bearer'
  | 'context_stale'
  | 'suppressed'
  | 'session_transition'
  | 'flag_disabled'
  | 'refresh_transient'
  | 'fresh_token'
  | 'second_401'
  | 'session_invalid'
  | 'no_token';

/**
 * The eleven valid (route, reason) pairs - a discriminated union so an
 * unlisted combination (e.g. `{ route: 'logout', reason: 'header_missing' }`)
 * fails to compile rather than surfacing only at runtime in Datadog.
 *
 * - `rejected`: the original 401 is rejected non-destructively — no retry,
 *   no legacy delegation, no logout attempted.
 * - `legacy`: delegated to the legacy (non-cache) handler.
 * - `retry_requested`: the handler accepted a fresh token and handed the
 *   retry to axios — the retry's own request interceptor may still cancel
 *   it on a stale pin, which stays on that interceptor's existing plain log
 *   line, not this one. This route means "requested", not "succeeded".
 * - `logout`: the handler made the request to log out — the shared
 *   throttle/cooldown in `debouncedExpiryLogout` may still suppress that
 *   request (e.g. a logout already in flight, or within the cooldown
 *   window); this route does not mean the logout necessarily executed.
 */
export type Auth401RecoveryRouteReason =
  | {
      route: 'rejected';
      reason:
        | 'header_missing'
        | 'no_bearer'
        | 'context_stale'
        | 'suppressed'
        | 'session_transition'
        | 'refresh_transient';
    }
  | { route: 'legacy'; reason: 'flag_disabled' }
  | { route: 'retry_requested'; reason: 'fresh_token' }
  | { route: 'logout'; reason: 'second_401' | 'session_invalid' | 'no_token' };

export type Auth401RecoveryRoute = Auth401RecoveryRouteReason & {
  /** The captured context's provider string — never a token or identifier. */
  provider: string;
  platform: 'desktop' | 'web';
  clientName: string;
};

/**
 * One structured log per terminal decision of the recovery-eligible 401
 * handler. A trailing payload (not a `child()` binding, not a message
 * object) so each route+reason combination gets its own Datadog burst-cap
 * budget instead of sharing one budget under a constant message. Never
 * throws — the logger owns transport isolation, so callers need no
 * try/catch around this.
 */
export function recordAuth401RecoveryRoute({
  route,
  reason,
  provider,
  platform,
  clientName,
}: Auth401RecoveryRoute): void {
  getRouteLogger().info('[auth-401-recovery] route', {
    event_name: 'auth_401_recovery_route',
    route,
    reason,
    provider,
    platform,
    client_name: clientName,
  });
}
